import React, { useState, useEffect, useCallback, useRef } from 'react';

const CONTEXT_API = 'https://api.botanix.in/api/sessions/ai/context';
const CLAUDE_API = 'https://api.botanix.in/api/sessions/ai/chat';

const C = {
  primary:'#153D4B', primaryLight:'#1E5A70',
  gold:'#C9A84C', goldLight:'#F5EDD4',
  accent:'#3AAF58', accentLight:'#EAF7EE',
  bg:'#0D1B2A', surface:'#112233', surface2:'#162840',
  border:'rgba(255,255,255,.08)', border2:'rgba(255,255,255,.12)',
  text:'#F0F6FF', text2:'#A8BFD0', text3:'#607080',
  red:'#EF4444', redBg:'rgba(239,68,68,.1)',
  amber:'#F59E0B', amberBg:'rgba(245,158,11,.1)',
  green:'#10B981', greenBg:'rgba(16,185,129,.1)',
  blue:'#3B82F6', blueBg:'rgba(59,130,246,.1)',
};

const inr = n => '₹' + Number(Math.round(n)||0).toLocaleString('en-IN');

const SYSTEM_PROMPT = (context) => `You are Botanix Intelligence OS — the AI brain of Botanix Nature Resort, Gurgaon. You are a world-class hospitality AI assistant with deep expertise in resort management, weddings, MICE, F&B, revenue management, and operations.

CURRENT LIVE DATA FROM BOTANIX ERP:
${JSON.stringify(context, null, 2)}

YOUR ROLE:
- L1 AI Insight: Analyze data and surface key findings
- L2 AI Copilot: Recommend specific, actionable next steps
- You speak directly to resort management (owner, GM, department heads)
- Be concise, specific, and actionable — not generic
- Always reference actual numbers from the data above
- Prioritize revenue, guest experience, and operational efficiency
- When you see issues, explain WHY and WHAT TO DO
- Format responses clearly with emojis for quick scanning
- For financial decisions above ₹50,000, always recommend human approval
- Never make up data — only use what's in the context above

BOTANIX CONTEXT:
- Premium nature resort in Aravallis, Sohna Road, Gurgaon
- Specializes in weddings, MICE, corporate outings, weekend getaways
- Has horticulture/farm, multiple venues, restaurant, activities
- Target: High-value weddings (₹15L-50L), corporate groups, weekend families

Respond in a helpful, professional yet warm tone. Be the smartest team member in the room.`;

const QUICK_PROMPTS = [
  { label:'Morning Brief', icon:'🌅', prompt:'Give me a complete morning briefing for today. What happened overnight, what needs attention today, and what should I focus on this morning?' },
  { label:'Revenue Analysis', icon:'💰', prompt:'Analyze our current revenue performance. How are we doing vs last month? What are the risks and opportunities? Give me specific actions to take.' },
  { label:'Lead Intelligence', icon:'🎯', prompt:'Which leads need my attention right now? Score them by priority and tell me exactly what to do with each one. Who is most likely to convert?' },
  { label:'Operations Alert', icon:'⚡', prompt:'What operational issues need immediate attention today? Check occupancy, inventory, pending tasks, refunds, and kitchen. What could go wrong?' },
  { label:'Wedding Pipeline', icon:'💍', prompt:'Give me a full analysis of our wedding pipeline. Which enquiries are hot? What is our conversion rate? What actions should the sales team take today?' },
  { label:'Inventory Risk', icon:'📦', prompt:'What inventory items are at risk? What needs to be ordered urgently? Give me a priority purchase list with quantities.' },
  { label:'Revenue Forecast', icon:'📈', prompt:'Based on current bookings and pipeline, forecast our revenue for the next 30 days. What can we do to increase it?' },
  { label:'Guest Intelligence', icon:'👥', prompt:'Who are our upcoming guests? Any VIPs, repeat guests, or special occasions? What should front office prepare?' },
];

const AGENTS = [
  { id:'brain', name:'Botanix Brain', icon:'🧠', desc:'Overall enterprise intelligence', color:C.gold },
  { id:'revenue', name:'Revenue AI', icon:'💰', desc:'Pricing & occupancy optimization', color:'#10B981' },
  { id:'sales', name:'Sales AI', icon:'🎯', desc:'CRM, leads & conversion', color:'#3B82F6' },
  { id:'wedding', name:'Wedding AI', icon:'💍', desc:'Events & wedding intelligence', color:'#8B5CF6' },
  { id:'kitchen', name:'Kitchen AI', icon:'🍽️', desc:'F&B demand & cost', color:'#F59E0B' },
  { id:'ops', name:'Ops AI', icon:'⚙️', desc:'Operations & housekeeping', color:'#EF4444' },
];

const AGENT_PROMPTS = {
  brain: 'You are Botanix Brain — the master enterprise AI. Analyze the complete business holistically.',
  revenue: 'You are Revenue AI. Focus exclusively on pricing, occupancy, ADR, RevPAR, and revenue optimization strategies.',
  sales: 'You are Sales AI. Focus on leads, conversion, CRM actions, follow-ups, and sales pipeline management.',
  wedding: 'You are Wedding AI. Focus on wedding enquiries, event planning, packages, profitability, and wedding sales.',
  kitchen: 'You are Kitchen AI. Focus on F&B demand forecasting, food cost, inventory, menu performance, and kitchen operations.',
  ops: 'You are Ops AI. Focus on housekeeping, maintenance, room readiness, staff, and operational efficiency.',
};

export default function BotanixAI({ setTab, defaultTab }) {
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [thinking, setThinking] = useState(false);
  const [activeAgent, setActiveAgent] = useState('brain');
  const [view, setView] = useState('command'); // command, chat, insights
  const [insights, setInsights] = useState([]);
  const [generatingInsights, setGeneratingInsights] = useState(false);
  const [contextError, setContextError] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const loadContext = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(CONTEXT_API);
      const d = await r.json();
      if (d.error) { setContextError(d.error); }
      else { setContext(d); setContextError(''); }
    } catch(e) { setContextError('Could not load ERP data'); }
    setLoading(false);
  }, []);

  useEffect(() => { loadContext(); }, [loadContext]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior:'smooth' }); }, [messages]);

  const callClaude = async (userMessage, systemOverride) => {
    if (!context) return 'ERP data not loaded yet. Please refresh.';
    setThinking(true);
    try {
      const agentPrompt = AGENT_PROMPTS[activeAgent] || '';
      const systemMsg = systemOverride || (SYSTEM_PROMPT(context) + '\n\nACTIVE AGENT: ' + agentPrompt);

      const history = messages.slice(-10).map(m => ({
        role: m.role,
        content: m.content,
      }));

      const r = await fetch(CLAUDE_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          system: systemMsg,
          messages: [...history, { role:'user', content: userMessage }],
          agent: activeAgent,
        }),
      });

      const d = await r.json();
      if (d.error) return `Error: ${d.error.message}`;
      return d.content?.[0]?.text || 'No response';
    } catch(e) {
      return `Error connecting to AI: ${e.message}`;
    } finally {
      setThinking(false);
    }
  };

  const sendMessage = async (msg) => {
    const userMsg = msg || input.trim();
    if (!userMsg) return;
    setInput('');
    setView('chat');

    const userMessage = { role:'user', content: userMsg, timestamp: new Date() };
    setMessages(p => [...p, userMessage]);

    const response = await callClaude(userMsg);
    const aiMessage = { role:'assistant', content: response, timestamp: new Date(), agent: activeAgent };
    setMessages(p => [...p, aiMessage]);
  };

  const generateInsights = async () => {
    if (!context) return;
    setGeneratingInsights(true);
    setView('insights');
    setInsights([]);

    const insightPrompts = [
      { category:'Revenue', icon:'💰', color:C.green, prompt:'In 2-3 sentences, what is the most important revenue insight from the data? Include the specific number and one action.' },
      { category:'Sales Alert', icon:'🎯', color:C.blue, prompt:'In 2-3 sentences, what is the most urgent sales/lead action needed right now? Be specific.' },
      { category:'Operations', icon:'⚙️', color:C.amber, prompt:'In 2-3 sentences, what operational issue needs attention today? Include specific numbers.' },
      { category:'Opportunity', icon:'🚀', color:C.gold, prompt:'In 2-3 sentences, what is the biggest revenue opportunity visible in the data right now?' },
      { category:'Risk Alert', icon:'⚠️', color:C.red, prompt:'In 2-3 sentences, what is the biggest risk to the business visible in the data? What should be done?' },
    ];

    for (const insight of insightPrompts) {
      const response = await callClaude(insight.prompt);
      setInsights(p => [...p, { ...insight, text: response }]);
    }
    setGeneratingInsights(false);
  };

  const clearChat = () => { setMessages([]); setView('command'); };

  // Stats from context
  const stats = context ? {
    occupancy: context.operations?.occupancy_pct || 0,
    todayArrivals: context.bookings?.today_arrivals?.length || 0,
    thisMonthRevenue: parseFloat(context.bookings?.revenue?.this_month || 0),
    lastMonthRevenue: parseFloat(context.bookings?.revenue?.last_month || 0),
    lowStockItems: context.inventory?.low_stock_items?.length || 0,
    pendingRefunds: parseInt(context.operations?.pending_refunds?.pending || 0),
    leadsNeedingFollowup: context.leads?.needs_followup?.length || 0,
    pipelineValue: parseFloat(context.leads?.total_pipeline || 0),
    upcomingBookings: context.bookings?.upcoming_7_days?.length || 0,
  } : {};

  const revenueChange = stats.lastMonthRevenue > 0
    ? Math.round((stats.thisMonthRevenue - stats.lastMonthRevenue) / stats.lastMonthRevenue * 100)
    : 0;

  return (
    <div style={{background:C.bg, minHeight:'100vh', color:C.text, fontFamily:'system-ui,sans-serif'}}>

      {/* Header */}
      <div style={{background:`linear-gradient(135deg,${C.primary} 0%,${C.primaryLight} 100%)`,padding:'20px 28px',borderBottom:`1px solid ${C.border}`}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div style={{display:'flex',alignItems:'center',gap:14}}>
            <div style={{width:44,height:44,borderRadius:12,background:`linear-gradient(135deg,${C.gold},#E8C97A)`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:22}}>🧠</div>
            <div>
              <div style={{fontSize:20,fontWeight:800,color:'#fff'}}>Botanix Intelligence OS</div>
              <div style={{fontSize:12,color:'rgba(255,255,255,.6)',marginTop:2}}>
                {loading ? 'Loading ERP data...' : contextError ? `⚠️ ${contextError}` : `✅ Live data — ${new Date(context?.generated_at||Date.now()).toLocaleTimeString('en-IN')}`}
              </div>
            </div>
          </div>
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <button onClick={loadContext} style={{padding:'7px 14px',background:'rgba(255,255,255,.1)',border:`1px solid ${C.border2}`,borderRadius:8,color:'rgba(255,255,255,.8)',fontSize:12,cursor:'pointer'}}>↻ Refresh</button>
            {messages.length>0 && <button onClick={clearChat} style={{padding:'7px 14px',background:'rgba(255,255,255,.1)',border:`1px solid ${C.border2}`,borderRadius:8,color:'rgba(255,255,255,.8)',fontSize:12,cursor:'pointer'}}>New Chat</button>}
          </div>
        </div>

        {/* Agent Selector */}
        <div style={{display:'flex',gap:8,marginTop:16,overflowX:'auto',paddingBottom:4}}>
          {AGENTS.map(agent=>(
            <button key={agent.id} onClick={()=>setActiveAgent(agent.id)}
              style={{flexShrink:0,padding:'7px 14px',background:activeAgent===agent.id?`${agent.color}33`:'rgba(255,255,255,.05)',
                border:`1px solid ${activeAgent===agent.id?agent.color:C.border}`,
                borderRadius:8,color:activeAgent===agent.id?agent.color:'rgba(255,255,255,.6)',
                fontSize:12,fontWeight:600,cursor:'pointer',whiteSpace:'nowrap'}}>
              {agent.icon} {agent.name}
            </button>
          ))}
        </div>
      </div>

      <div style={{padding:'20px 28px',maxWidth:1100,margin:'0 auto'}}>

        {/* View selector */}
        <div style={{display:'flex',gap:8,marginBottom:20}}>
          {[['command','🏠 Command Centre'],['chat','💬 Ask Botanix'],['insights','⚡ AI Insights']].map(([id,label])=>(
            <button key={id} onClick={()=>setView(id)}
              style={{padding:'8px 16px',background:view===id?C.gold:'transparent',color:view===id?'#1A1A1A':C.text2,
                border:`1px solid ${view===id?C.gold:C.border}`,borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer'}}>
              {label}
            </button>
          ))}
          <button onClick={generateInsights} disabled={generatingInsights||loading}
            style={{marginLeft:'auto',padding:'8px 16px',background:`linear-gradient(135deg,${C.primary},${C.primaryLight})`,
              color:C.gold,border:`1px solid ${C.gold}`,borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer',
              opacity:generatingInsights||loading?0.6:1}}>
            {generatingInsights?'⏳ Analyzing...':'⚡ Generate Insights'}
          </button>
        </div>

        {/* ── COMMAND CENTRE VIEW ── */}
        {view==='command' && (
          <div>
            {/* KPI Strip */}
            {context && (
              <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:10,marginBottom:20}}>
                {[
                  {label:'Occupancy',value:`${stats.occupancy}%`,icon:'🏨',color:stats.occupancy>70?C.green:stats.occupancy>40?C.amber:C.red},
                  {label:'Today Arrivals',value:stats.todayArrivals,icon:'🚗',color:C.blue},
                  {label:'Month Revenue',value:inr(stats.thisMonthRevenue),icon:'💰',color:C.green,sub:revenueChange>0?`▲${revenueChange}%`:revenueChange<0?`▼${Math.abs(revenueChange)}%`:''},
                  {label:'Pipeline',value:inr(stats.pipelineValue),icon:'🎯',color:C.gold},
                  {label:'Low Stock',value:stats.lowStockItems,icon:'📦',color:stats.lowStockItems>5?C.red:stats.lowStockItems>0?C.amber:C.green},
                  {label:'Pending Refunds',value:stats.pendingRefunds,icon:'💸',color:stats.pendingRefunds>0?C.amber:C.green},
                ].map((kpi,i)=>(
                  <div key={i} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:12,padding:'14px 16px'}}>
                    <div style={{fontSize:18,marginBottom:6}}>{kpi.icon}</div>
                    <div style={{fontSize:kpi.value?.toString().length>6?16:22,fontWeight:900,color:kpi.color}}>{kpi.value}</div>
                    {kpi.sub && <div style={{fontSize:10,color:kpi.color,fontWeight:700}}>{kpi.sub} vs last month</div>}
                    <div style={{fontSize:10,color:C.text3,marginTop:2,textTransform:'uppercase',letterSpacing:'.05em'}}>{kpi.label}</div>
                  </div>
                ))}
              </div>
            )}

            {/* AI Alerts */}
            {context && (
              <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:20,marginBottom:20}}>
                <div style={{fontSize:14,fontWeight:800,color:C.gold,marginBottom:14}}>🤖 AI Attention Required</div>
                <div style={{display:'grid',gap:8}}>
                  {stats.leadsNeedingFollowup > 0 && (
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',background:C.redBg,borderRadius:10,border:`1px solid ${C.red}33`}}>
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <span style={{fontSize:16}}>🔴</span>
                        <div>
                          <div style={{fontSize:13,fontWeight:700,color:C.text}}>{stats.leadsNeedingFollowup} leads need follow-up right now</div>
                          <div style={{fontSize:11,color:C.text3}}>Pipeline value: {inr(stats.pipelineValue)} at risk</div>
                        </div>
                      </div>
                      <button onClick={()=>sendMessage('Which leads need follow-up urgently? Give me names, phone numbers, and exactly what to say to each one.')}
                        style={{padding:'5px 12px',background:C.red,color:'#fff',border:'none',borderRadius:6,fontSize:11,fontWeight:700,cursor:'pointer'}}>Ask AI →</button>
                    </div>
                  )}

                  {stats.upcomingBookings > 0 && (
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',background:C.amberBg,borderRadius:10,border:`1px solid ${C.amber}33`}}>
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <span style={{fontSize:16}}>🟠</span>
                        <div>
                          <div style={{fontSize:13,fontWeight:700,color:C.text}}>{stats.upcomingBookings} bookings arriving next 7 days</div>
                          <div style={{fontSize:11,color:C.text3}}>
                            {context.bookings.upcoming_7_days.slice(0,2).map(b=>`${b.guest_name||b.booking_code} (${new Date(b.start_date).toLocaleDateString('en-IN')})`).join(' · ')}
                          </div>
                        </div>
                      </div>
                      <button onClick={()=>sendMessage('Tell me about all upcoming bookings in next 7 days. Any VIPs, special occasions, or preparation needed?')}
                        style={{padding:'5px 12px',background:C.amber,color:'#1A1A1A',border:'none',borderRadius:6,fontSize:11,fontWeight:700,cursor:'pointer'}}>Prepare →</button>
                    </div>
                  )}

                  {stats.lowStockItems > 0 && (
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',background:C.amberBg,borderRadius:10,border:`1px solid ${C.amber}33`}}>
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <span style={{fontSize:16}}>🟠</span>
                        <div>
                          <div style={{fontSize:13,fontWeight:700,color:C.text}}>{stats.lowStockItems} inventory items below reorder level</div>
                          <div style={{fontSize:11,color:C.text3}}>{context.inventory.low_stock_items.slice(0,3).map(i=>i.item_name).join(', ')}{stats.lowStockItems>3?'...':''}</div>
                        </div>
                      </div>
                      <button onClick={()=>sendMessage('Create a priority purchase list for all low stock items. Include urgency level and recommended order quantities.')}
                        style={{padding:'5px 12px',background:C.amber,color:'#1A1A1A',border:'none',borderRadius:6,fontSize:11,fontWeight:700,cursor:'pointer'}}>Order List →</button>
                    </div>
                  )}

                  {stats.pendingRefunds > 0 && (
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',background:C.redBg,borderRadius:10,border:`1px solid ${C.red}33`}}>
                      <div style={{display:'flex',alignItems:'center',gap:10}}>
                        <span style={{fontSize:16}}>🔴</span>
                        <div>
                          <div style={{fontSize:13,fontWeight:700,color:C.text}}>{stats.pendingRefunds} refund(s) pending approval</div>
                          <div style={{fontSize:11,color:C.text3}}>Total pending: {inr(context.operations.pending_refunds?.total_pending||0)}</div>
                        </div>
                      </div>
                      <button onClick={()=>setTab&&setTab('refunds')}
                        style={{padding:'5px 12px',background:C.red,color:'#fff',border:'none',borderRadius:6,fontSize:11,fontWeight:700,cursor:'pointer'}}>Review →</button>
                    </div>
                  )}

                  {revenueChange < -10 && (
                    <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:C.redBg,borderRadius:10,border:`1px solid ${C.red}33`}}>
                      <span style={{fontSize:16}}>🔴</span>
                      <div>
                        <div style={{fontSize:13,fontWeight:700,color:C.text}}>Revenue down {Math.abs(revenueChange)}% vs last month</div>
                        <div style={{fontSize:11,color:C.text3}}>{inr(stats.thisMonthRevenue)} this month vs {inr(stats.lastMonthRevenue)} last month</div>
                      </div>
                    </div>
                  )}

                  {revenueChange >= 0 && stats.lowStockItems===0 && stats.pendingRefunds===0 && stats.leadsNeedingFollowup===0 && (
                    <div style={{display:'flex',alignItems:'center',gap:10,padding:'10px 14px',background:C.greenBg,borderRadius:10,border:`1px solid ${C.green}33`}}>
                      <span style={{fontSize:16}}>🟢</span>
                      <div style={{fontSize:13,color:C.text}}>Operations looking good! Revenue {revenueChange>0?`up ${revenueChange}%`:' stable'} vs last month.</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Quick Prompts */}
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:20,marginBottom:20}}>
              <div style={{fontSize:14,fontWeight:800,color:C.text,marginBottom:14}}>💬 Ask Botanix AI</div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8,marginBottom:14}}>
                {QUICK_PROMPTS.map((qp,i)=>(
                  <button key={i} onClick={()=>sendMessage(qp.prompt)}
                    style={{padding:'12px 14px',background:C.surface2,border:`1px solid ${C.border}`,borderRadius:10,
                      color:C.text2,fontSize:12,fontWeight:600,cursor:'pointer',textAlign:'left',
                      transition:'all .2s'}}>
                    <div style={{fontSize:18,marginBottom:6}}>{qp.icon}</div>
                    <div>{qp.label}</div>
                  </button>
                ))}
              </div>

              {/* Input */}
              <div style={{display:'flex',gap:8}}>
                <input ref={inputRef} value={input} onChange={e=>setInput(e.target.value)}
                  onKeyDown={e=>e.key==='Enter'&&!e.shiftKey&&sendMessage()}
                  placeholder={`Ask ${AGENTS.find(a=>a.id===activeAgent)?.name||'Botanix AI'} anything about your resort...`}
                  style={{flex:1,padding:'12px 16px',background:C.surface2,border:`1px solid ${C.border2}`,borderRadius:10,
                    color:C.text,fontSize:14,outline:'none'}} />
                <button onClick={()=>sendMessage()} disabled={thinking||!input.trim()}
                  style={{padding:'12px 20px',background:thinking?C.surface2:`linear-gradient(135deg,${C.gold},#E8C97A)`,
                    color:thinking?C.text3:'#1A1A1A',border:'none',borderRadius:10,fontSize:14,fontWeight:700,
                    cursor:thinking?'default':'pointer',minWidth:80}}>
                  {thinking?'⏳':'Send'}
                </button>
              </div>
            </div>

            {/* Upcoming bookings */}
            {context?.bookings?.upcoming_7_days?.length > 0 && (
              <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:20}}>
                <div style={{fontSize:14,fontWeight:800,color:C.text,marginBottom:14}}>📅 Next 7 Days</div>
                <div style={{display:'grid',gap:8}}>
                  {context.bookings.upcoming_7_days.slice(0,5).map(b=>(
                    <div key={b.id} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',background:C.surface2,borderRadius:8}}>
                      <div>
                        <div style={{fontSize:13,fontWeight:700,color:C.text}}>{b.guest_name||'Guest'}</div>
                        <div style={{fontSize:11,color:C.text3}}>{b.booking_code} · {b.booking_type?.replace(/[^\w\s]/g,'').trim()} · {b.pax_count} pax</div>
                      </div>
                      <div style={{textAlign:'right'}}>
                        <div style={{fontSize:13,fontWeight:700,color:C.gold}}>{inr(b.total_amount)}</div>
                        <div style={{fontSize:11,color:C.text3}}>{new Date(b.start_date).toLocaleDateString('en-IN')} → {new Date(b.end_date).toLocaleDateString('en-IN')}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── CHAT VIEW ── */}
        {view==='chat' && (
          <div>
            <div style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,overflow:'hidden',marginBottom:16}}>
              <div style={{maxHeight:500,overflowY:'auto',padding:20}}>
                {messages.length===0 && (
                  <div style={{textAlign:'center',padding:40,color:C.text3}}>
                    <div style={{fontSize:40,marginBottom:12}}>🧠</div>
                    <div style={{fontSize:15,fontWeight:600,color:C.text2,marginBottom:8}}>Ask Botanix AI anything</div>
                    <div style={{fontSize:13}}>I have access to your live ERP data — bookings, leads, inventory, revenue and more.</div>
                  </div>
                )}
                {messages.map((msg,i)=>(
                  <div key={i} style={{marginBottom:16,display:'flex',flexDirection:msg.role==='user'?'row-reverse':'row',gap:10,alignItems:'flex-start'}}>
                    <div style={{width:32,height:32,borderRadius:'50%',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,
                      background:msg.role==='user'?C.primary:`linear-gradient(135deg,${C.gold},#E8C97A)`}}>
                      {msg.role==='user'?'👤':'🧠'}
                    </div>
                    <div style={{maxWidth:'80%',padding:'12px 16px',borderRadius:12,
                      background:msg.role==='user'?C.primary:C.surface2,
                      border:`1px solid ${msg.role==='user'?C.primaryLight:C.border}`}}>
                      <div style={{fontSize:13,lineHeight:1.7,color:C.text,whiteSpace:'pre-wrap'}}>{msg.content}</div>
                      <div style={{fontSize:10,color:C.text3,marginTop:6,display:'flex',alignItems:'center',gap:6}}>
                        {msg.role==='assistant' && <span style={{color:AGENTS.find(a=>a.id===msg.agent)?.color||C.gold,fontWeight:700}}>{AGENTS.find(a=>a.id===msg.agent)?.icon} {AGENTS.find(a=>a.id===msg.agent)?.name}</span>}
                        <span>{new Date(msg.timestamp).toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})}</span>
                      </div>
                    </div>
                  </div>
                ))}
                {thinking && (
                  <div style={{display:'flex',gap:10,alignItems:'center',marginBottom:16}}>
                    <div style={{width:32,height:32,borderRadius:'50%',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:16}}>🧠</div>
                    <div style={{padding:'12px 16px',background:C.surface2,border:`1px solid ${C.border}`,borderRadius:12}}>
                      <div style={{display:'flex',gap:4}}>
                        {[0,1,2].map(i=>(
                          <div key={i} style={{width:6,height:6,borderRadius:'50%',background:C.gold,animation:`pulse ${0.6+i*0.2}s infinite alternate`}} />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Quick prompts in chat */}
            <div style={{display:'flex',gap:6,marginBottom:12,flexWrap:'wrap'}}>
              {QUICK_PROMPTS.slice(0,4).map((qp,i)=>(
                <button key={i} onClick={()=>sendMessage(qp.prompt)}
                  style={{padding:'6px 12px',background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,color:C.text2,fontSize:12,cursor:'pointer'}}>
                  {qp.icon} {qp.label}
                </button>
              ))}
            </div>

            <div style={{display:'flex',gap:8}}>
              <input value={input} onChange={e=>setInput(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&!e.shiftKey&&sendMessage()}
                placeholder="Ask anything about Botanix..."
                style={{flex:1,padding:'12px 16px',background:C.surface,border:`1px solid ${C.border2}`,borderRadius:10,color:C.text,fontSize:14,outline:'none'}} />
              <button onClick={()=>sendMessage()} disabled={thinking||!input.trim()}
                style={{padding:'12px 20px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:10,fontSize:14,fontWeight:700,cursor:'pointer',minWidth:80,opacity:thinking||!input.trim()?0.5:1}}>
                {thinking?'⏳':'Send'}
              </button>
            </div>
          </div>
        )}

        {/* ── INSIGHTS VIEW ── */}
        {view==='insights' && (
          <div>
            {insights.length===0 && !generatingInsights && (
              <div style={{textAlign:'center',padding:60,background:C.surface,borderRadius:14,border:`1px solid ${C.border}`}}>
                <div style={{fontSize:40,marginBottom:12}}>⚡</div>
                <div style={{fontSize:15,fontWeight:600,color:C.text2,marginBottom:8}}>AI Insights Ready</div>
                <div style={{fontSize:13,color:C.text3,marginBottom:20}}>Click "Generate Insights" to get AI analysis of your current ERP data</div>
                <button onClick={generateInsights}
                  style={{padding:'12px 24px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:10,fontSize:14,fontWeight:700,cursor:'pointer'}}>
                  ⚡ Generate AI Insights Now
                </button>
              </div>
            )}

            {generatingInsights && insights.length===0 && (
              <div style={{textAlign:'center',padding:60,color:C.text3}}>
                <div style={{fontSize:32,marginBottom:12}}>🧠</div>
                <div style={{fontSize:14}}>AI is analyzing your ERP data...</div>
              </div>
            )}

            <div style={{display:'grid',gap:14}}>
              {insights.map((insight,i)=>(
                <div key={i} style={{background:C.surface,border:`1px solid ${C.border}`,borderRadius:14,padding:20,borderLeft:`4px solid ${insight.color}`}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
                    <span style={{fontSize:20}}>{insight.icon}</span>
                    <span style={{fontSize:13,fontWeight:800,color:insight.color,textTransform:'uppercase',letterSpacing:'.05em'}}>{insight.category}</span>
                  </div>
                  <div style={{fontSize:14,lineHeight:1.7,color:C.text,whiteSpace:'pre-wrap'}}>{insight.text}</div>
                  <button onClick={()=>sendMessage(`Tell me more about this: ${insight.text}`)}
                    style={{marginTop:10,padding:'5px 12px',background:'transparent',border:`1px solid ${insight.color}`,borderRadius:6,color:insight.color,fontSize:11,fontWeight:600,cursor:'pointer'}}>
                    Dig Deeper →
                  </button>
                </div>
              ))}

              {generatingInsights && insights.length > 0 && (
                <div style={{textAlign:'center',padding:16,color:C.text3,fontSize:13}}>⏳ Generating more insights...</div>
              )}
            </div>

            {insights.length > 0 && !generatingInsights && (
              <div style={{marginTop:16,display:'flex',gap:10}}>
                <button onClick={generateInsights}
                  style={{padding:'10px 20px',background:C.surface,border:`1px solid ${C.gold}`,borderRadius:8,color:C.gold,fontSize:13,fontWeight:700,cursor:'pointer'}}>
                  ↻ Refresh Insights
                </button>
                <button onClick={()=>{setView('chat');sendMessage('Based on all the AI insights you just generated, what are the top 3 actions I should take right now as the owner/GM?');}}
                  style={{padding:'10px 20px',background:`linear-gradient(135deg,${C.gold},#E8C97A)`,color:'#1A1A1A',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:'pointer'}}>
                  Get Top 3 Actions →
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes pulse { from { opacity: .4; transform: scale(.8); } to { opacity: 1; transform: scale(1.2); } }
        input:focus { border-color: ${C.gold} !important; }
      `}</style>
    </div>
  );
}
